import { Permission } from '@frontend/authorization';
import { ClassType, EntityType } from '@frontend/common';
import { HorizontalButtonGroupButton, Subnav } from '@frontend/components/common';
import { ObjectListHeightModeProvider } from '@frontend/components/list';
import { ConfirmationModal } from '@frontend/components/modal';
import { Spinner } from '@frontend/elements';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';

import { ObjectDetailMapper } from '../object-detail-mapper';
import ObjectDetailButtonGroup from './object-detail-button-group/object-detail-button-group.component';
import useObjectDetail from './object-detail.controller';

export interface ObjectDetailProps<T> {
    fetch: (...args: any) => Promise<T>;
    delete?: {
        onDelete: (...args: any) => Promise<void>;
        onDeleteSuccess?: () => void;
        propertyName?: React.ReactNode;
        displayName?: (obj: T) => React.ReactNode;
        requiredPermissions?: (obj: T) => Permission[];
    };
    update?: {
        onUpdate: () => void;
        requiredPermissions?: (obj: T) => Permission[];
    };
    recovery?: {
        onRecover: (obj: T) => void;
        requiredPermissions?: (obj: T) => Permission[];
    };
    /**
     * @description keys should be provided in the correct order to use the fetch and delete function
     */
    urlParamKeys: string[];
    /**
     * @description is used to route back in case of any failure or on delete
     */
    baseRoute: string;
    /**
     * @description is used to map navigation keys to their replacement values for URL construction
     */
    navigationKeyReplacements?: { [key: string]: string };
    updateKeys?: string[];
    eventListener?: EntityType;
    dispatch?: ThunkDispatch<any, any, Action>;
    id: string;
    loadingTitle?: React.ReactNode;
    objectHeader: (value: T) => React.ReactNode;
    /**
     * @description is used to map object keys to display
     */
    order: (keyof T)[] | ((obj: T) => (keyof T)[]);
    /**
     * @description All children should be a DetailItem. If you want to display multiple children then wrap them inside a fragment or div
     * @description If you want to display a subsection on the Main page then set the main prop to true (max 1)
     * @example <DetailItem identifier='main' main><div>Children</div></DetailItem>
     * @example <> <DetailItem identifier='main' main><div>Children</div></DetailItem> <DetailItem identifier='linked'><div>Children</div></DetailItem> </>
     */
    children?: (entity: T) => React.ReactNode;
    object?: (entity: T | null) => void;
    cardClassName?: string;
    customHeaderButtons?: (entity: T) => HorizontalButtonGroupButton[];
    customHeaderComponents?: (obj: T) => React.ReactNode;
    valueOverwrite?: Map<keyof T, (obj: T) => React.ReactNode>;
    keyOverwrite?: Map<string | keyof T, React.ReactNode>;
    objectBadgeKeyOverwrite?: Map<keyof T, string>;
    entityType?: EntityType;
    /**
     * When true, subscribe to the single entity via useReactiveObject and refetch
     * whenever that entity updates (MQTT) or the URL id changes.
     * Default/false keeps the legacy collection-topic subscription (platform-safe).
     */
    reactive?: boolean;
}
export const ObjectDetail = <T extends { id: string }>(props: ObjectDetailProps<T>) => {
    const viewProps = useObjectDetail<T>(props);

    if (!viewProps.object) {
        return (
            <div id={props.id}>
                <div className='card mt-3'>
                    <div className='card-header'>{props.loadingTitle}</div>
                    <div className='card-body'>
                        <Spinner />
                    </div>
                </div>
            </div>
        );
    }
    return (
        <ObjectListHeightModeProvider mode='shrink'>
            {viewProps.filteredSubSections.length > 0 && (
                <Subnav
                    activeSubsection={viewProps.activeSubSection}
                    onChange={viewProps.onChange}
                    subnavItems={viewProps.subSections}
                />
            )}
            {!viewProps.activeSubSection || viewProps.activeSubSection === 'main' || viewProps.activeSubSection === 'Main' ? (
                <div
                    id={props.id}
                    className='flex w-full flex-row flex-wrap justify-between gap-2'>
                    <div className={`bg-background-surface ${props.cardClassName ? props.cardClassName : 'card mt-3 w-full'}`}>
                        <div className='card-header bg-background-surface text-foreground-default flex justify-between'>
                            <h4 data-testid='ObjectDetail.Header'>{props.objectHeader(viewProps.object)}</h4>
                            <ObjectDetailButtonGroup
                                buttons={[
                                    {
                                        hide: !props.delete,
                                        onClick: () =>
                                            viewProps.openModal(viewProps.deleteModalId, () => (
                                                <ConfirmationModal
                                                    handleClose={() => viewProps.closeModal(viewProps.deleteModalId)}
                                                    message={CommonMessage.FORMS.DELETE_OBJECT(
                                                        props.delete?.propertyName,
                                                        props.delete?.displayName ? props.delete.displayName(viewProps.object!) : ''
                                                    )}
                                                    onConfirm={viewProps.onDeleteConfirmed}
                                                    severity={ClassType.DANGER}
                                                    show={true}
                                                    cascadeWarning
                                                />
                                            )),
                                        icon: ICONS.BUTTON.DELETE,
                                        id: 'ObjectDetail.DeleteButton',
                                        text: CommonMessage.BUTTONS.DELETE,
                                        requiredPermissions: props.delete?.requiredPermissions?.(viewProps.object),
                                        className: 'btn btn-status-danger text-always-white'
                                    },
                                    {
                                        hide: !props.update,
                                        onClick: viewProps.onUpdate,
                                        icon: ICONS.BUTTON.UPDATE,
                                        id: 'ObjectDetail.UpdateButton',
                                        text: CommonMessage.BUTTONS.UPDATE,
                                        requiredPermissions: props.update?.requiredPermissions?.(viewProps.object),
                                        className: 'btn btn-status-info text-always-white'
                                    },
                                    {
                                        hide: !props.recovery || !(viewProps.object as any)?.delete_timestamp,
                                        onClick: () => props.recovery?.onRecover && viewProps.object && props.recovery?.onRecover(viewProps.object),
                                        icon: ICONS.BUTTON.RECOVER,
                                        id: 'ObjectDetail.RecoverButton',
                                        text: CommonMessage.BUTTONS.RECOVER,
                                        requiredPermissions: props.recovery?.requiredPermissions?.(viewProps.object),
                                        className: 'btn btn-status-success text-always-white'
                                    },
                                    ...(props.customHeaderButtons ? props.customHeaderButtons(viewProps.object) : [])
                                ]}
                            />
                        </div>
                        <div
                            data-testid='object-detail-div'
                            className='bg-background-surface flex flex-wrap justify-between p-4'>
                            <ObjectDetailMapper<T>
                                object={viewProps.object}
                                order={typeof props.order === 'function' ? props.order(viewProps.object) : props.order}
                                valueOverwrite={props.valueOverwrite}
                                keyOverwrite={props.keyOverwrite}
                                objectBadgeKeyOverwrite={props.objectBadgeKeyOverwrite}
                            />
                        </div>
                    </div>
                    {props.customHeaderComponents && props.customHeaderComponents(viewProps.object)}
                    {viewProps.main}
                </div>
            ) : (
                <div
                    id={props.id}
                    className='mt-3 flex flex-col gap-2'>
                    {viewProps.subSections.find((s) => s.identifier.toLowerCase() === viewProps.activeSubSection)?.children}
                </div>
            )}
        </ObjectListHeightModeProvider>
    );
};
